import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { FiPlus, FiPackage, FiBox, FiTool, FiUploadCloud, FiX, FiCheck } from "react-icons/fi";
import { FaIndianRupeeSign } from "react-icons/fa6";
import { useDropzone } from "react-dropzone";
import { baseurl } from "../../baseurl";
import axios from "axios";
import Swal from "sweetalert2";

const GST_SLABS = [0, 5, 12, 18, 28];
const UNIT_OPTIONS = ["Pcs", "Kgs", "Mtrs", "Hrs", "Ltrs", "Nos", "Sq.Ft"];

const DEFAULT_PRODUCT = {
  type: "Product",
  name: "",
  hsn: "",
  unit: "Pcs",
  rate: "",
  tax: "18",
  description: "",
  imageFile: null,
  imagePreview: "",
  minOrderQty: "",
  stockQty: "0",
  inventoryTracked: "false",
};

/* ─── CSS ─── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

:root {
  --ap-teal:      #0a5556;
  --ap-teal-mid:  #0c7172;
  --ap-teal-lite: #14b8a6;
  --ap-gold:      #FFD166;
  --ap-bg:        #eef6f6;
  --ap-white:     #ffffff;
  --ap-border:    rgba(10,85,86,0.12);
  --ap-text:      #0f2a2a;
  --ap-muted:     #5a8080;
  --ap-danger:    #ef4444;
  --ap-success:   #10b981;
}

@keyframes ap__fadeUp { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
@keyframes ap__shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
@keyframes ap__spin { to{transform:rotate(360deg)} }
@keyframes ap__pulse { 0%,100%{opacity:1} 50%{opacity:.5} }

.ap__wrap {
  font-family:'DM Sans',sans-serif;
  padding:22px 22px 40px;
  background:var(--ap-bg);
  min-height:100vh;
  animation:ap__fadeUp 0.4s ease both;
}

/* ── header ── */
.ap__hd { display:flex; align-items:center; gap:14px; margin-bottom:26px; }
.ap__hd-icon {
  width:50px; height:50px; border-radius:15px; flex-shrink:0;
  background:linear-gradient(135deg,var(--ap-teal),var(--ap-teal-lite));
  display:flex; align-items:center; justify-content:center;
  font-size:22px; color:#fff; box-shadow:0 6px 20px rgba(10,85,86,0.28);
}
.ap__hd-title { font-family:'Sora',sans-serif; font-size:21px; font-weight:800; color:var(--ap-text); letter-spacing:-0.03em; margin:0 0 3px; }
.ap__hd-sub   { font-size:12.5px; color:var(--ap-muted); margin:0; }

/* ── layout ── */
.ap__layout { display:grid; grid-template-columns:1fr 300px; gap:18px; align-items:start; }
@media(max-width:900px){ .ap__layout{ grid-template-columns:1fr; } }

/* ── card ── */
.ap__card {
  background:var(--ap-white); border-radius:20px;
  border:1px solid var(--ap-border);
  box-shadow:0 2px 20px rgba(10,85,86,0.07),0 8px 40px rgba(0,0,0,0.03);
  overflow:hidden; position:relative;
}
.ap__card::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--ap-teal) 0%,var(--ap-teal-lite) 55%,var(--ap-gold) 100%);
}
.ap__card-hd {
  display:flex; align-items:center; gap:10px;
  padding:16px 22px; border-bottom:1px solid var(--ap-border);
}
.ap__card-hd-icon {
  width:32px; height:32px; border-radius:9px; flex-shrink:0;
  background:linear-gradient(135deg,var(--ap-teal),var(--ap-teal-lite));
  display:flex; align-items:center; justify-content:center;
  font-size:14px; color:#fff; box-shadow:0 3px 10px rgba(10,85,86,0.22);
}
.ap__card-hd-title { font-family:'Sora',sans-serif; font-size:13px; font-weight:800; color:var(--ap-text); letter-spacing:-0.01em; }
.ap__card-body { padding:22px; }

/* ── type toggle ── */
.ap__type-row { display:flex; gap:10px; margin-bottom:4px; }
.ap__type-btn {
  flex:1; display:flex; align-items:center; justify-content:center; gap:8px;
  padding:11px 16px; border-radius:12px; cursor:pointer; font-size:13px;
  font-family:'Sora',sans-serif; font-weight:700; transition:all 0.18s;
  border:2px solid var(--ap-border); background:rgba(10,85,86,0.03); color:var(--ap-muted);
}
.ap__type-btn--active {
  border-color:var(--ap-teal); background:rgba(10,85,86,0.08); color:var(--ap-teal);
  box-shadow:0 3px 12px rgba(10,85,86,0.14);
}
.ap__type-btn:hover:not(.ap__type-btn--active) {
  border-color:rgba(10,85,86,0.25); color:var(--ap-teal-mid);
}

/* ── divider ── */
.ap__divider {
  height:1px; background:var(--ap-border); margin:20px 0;
  position:relative;
}
.ap__divider-label {
  position:absolute; top:50%; left:0; transform:translateY(-50%);
  font-family:'Sora',sans-serif; font-size:10px; font-weight:700; letter-spacing:0.08em;
  text-transform:uppercase; color:var(--ap-muted);
  background:var(--ap-white); padding:0 10px 0 0;
}

/* ── form grid ── */
.ap__grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.ap__grid-3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:14px; }
.ap__grid-4 { display:grid; grid-template-columns:1fr 1fr 1fr 1fr; gap:14px; }
@media(max-width:700px){
  .ap__grid-2,.ap__grid-3,.ap__grid-4{ grid-template-columns:1fr 1fr; }
}
@media(max-width:480px){
  .ap__grid-2,.ap__grid-3,.ap__grid-4{ grid-template-columns:1fr; }
}
.ap__full { grid-column:1/-1; }

/* ── field ── */
.ap__field { display:flex; flex-direction:column; gap:5px; }
.ap__label {
  font-family:'Sora',sans-serif; font-size:10.5px; font-weight:700;
  letter-spacing:0.07em; text-transform:uppercase; color:var(--ap-teal);
  display:flex; align-items:center; gap:4px;
}
.ap__label .ap__req { color:var(--ap-danger); }
.ap__input, .ap__select, .ap__textarea {
  padding:9px 13px; font-size:13.5px; font-family:'DM Sans',sans-serif;
  color:var(--ap-text); background:#f3fafa;
  border:1.5px solid rgba(10,85,86,0.14); border-radius:10px;
  outline:none; transition:border-color 0.18s, box-shadow 0.18s, background 0.18s;
  width:100%;
}
.ap__input:focus, .ap__select:focus, .ap__textarea:focus {
  border-color:var(--ap-teal); box-shadow:0 0 0 3.5px rgba(10,85,86,0.1); background:#fff;
}
.ap__input::placeholder, .ap__textarea::placeholder { color:#9ababa; }
.ap__select { appearance:none; cursor:pointer;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='%235a8080' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat:no-repeat; background-position:right 11px center; padding-right:32px;
  background-color:#f3fafa;
}
.ap__select:focus { background-color:#fff; }
.ap__textarea { resize:vertical; min-height:88px; }

/* amount input with icon */
.ap__amount-wrap { position:relative; }
.ap__amount-icon {
  position:absolute; left:12px; top:50%; transform:translateY(-50%);
  color:var(--ap-muted); font-size:13px; pointer-events:none;
  display:flex; align-items:center;
}
.ap__amount-wrap .ap__input { padding-left:30px; }

/* ── toggle switch (inventory tracked) ── */
.ap__toggle-row { display:flex; align-items:center; gap:12px; margin-top:4px; }
.ap__toggle-switch {
  position:relative; width:42px; height:22px; flex-shrink:0; cursor:pointer;
}
.ap__toggle-switch input { opacity:0; width:0; height:0; position:absolute; }
.ap__toggle-track {
  position:absolute; inset:0; border-radius:50px;
  background:#d4e8e8; transition:background 0.22s;
}
.ap__toggle-thumb {
  position:absolute; top:3px; left:3px;
  width:16px; height:16px; border-radius:50%; background:#fff;
  box-shadow:0 2px 6px rgba(0,0,0,0.18); transition:transform 0.22s;
}
.ap__toggle-switch input:checked ~ .ap__toggle-track { background:var(--ap-teal); }
.ap__toggle-switch input:checked ~ .ap__toggle-thumb { transform:translateX(20px); }
.ap__toggle-label { font-size:13px; color:var(--ap-text); font-weight:500; }

/* ── dropzone ── */
.ap__dropzone {
  border:2px dashed rgba(10,85,86,0.25); border-radius:14px;
  padding:22px 16px; text-align:center; cursor:pointer;
  background:#f7fdfd; transition:border-color 0.18s, background 0.18s;
}
.ap__dropzone:hover, .ap__dropzone--active {
  border-color:var(--ap-teal); background:rgba(10,85,86,0.04);
}
.ap__dropzone-icon { font-size:28px; color:var(--ap-teal-lite); margin-bottom:8px; }
.ap__dropzone-text { font-size:13px; color:var(--ap-muted); line-height:1.5; }
.ap__dropzone-text strong { color:var(--ap-teal); }

/* image preview */
.ap__img-preview {
  position:relative; display:inline-block; margin-bottom:12px;
}
.ap__img-preview img {
  width:100px; height:100px; object-fit:cover;
  border-radius:12px; border:2px solid var(--ap-border);
  box-shadow:0 4px 14px rgba(10,85,86,0.12);
}
.ap__img-remove {
  position:absolute; top:-6px; right:-6px;
  width:22px; height:22px; border-radius:50%; border:none; cursor:pointer;
  background:var(--ap-danger); color:#fff;
  display:flex; align-items:center; justify-content:center; font-size:11px;
  box-shadow:0 2px 8px rgba(239,68,68,0.35); transition:transform 0.15s;
}
.ap__img-remove:hover { transform:scale(1.15); }

/* ── side card: summary ── */
.ap__summary-row { display:flex; justify-content:space-between; align-items:center; padding:8px 0; border-bottom:1px solid var(--ap-border); }
.ap__summary-row:last-child { border-bottom:none; }
.ap__summary-lbl { font-size:12px; color:var(--ap-muted); }
.ap__summary-val { font-family:'Sora',sans-serif; font-size:13px; font-weight:700; color:var(--ap-text); }
.ap__summary-val--teal { color:var(--ap-teal); }

.ap__type-chip {
  display:inline-flex; align-items:center; gap:5px;
  font-size:11px; font-weight:700; font-family:'Sora',sans-serif;
  border-radius:50px; padding:3px 10px;
}
.ap__type-chip--product { background:rgba(10,85,86,0.09); color:var(--ap-teal); border:1px solid rgba(10,85,86,0.18); }
.ap__type-chip--service  { background:rgba(59,130,246,0.09); color:#2563eb; border:1px solid rgba(59,130,246,0.20); }

/* ── submit ── */
.ap__submit {
  width:100%; padding:13px; border-radius:12px; border:none; cursor:pointer;
  font-size:14px; font-family:'Sora',sans-serif; font-weight:800; color:#fff;
  background:linear-gradient(135deg,var(--ap-teal),var(--ap-teal-lite));
  box-shadow:0 4px 18px rgba(10,85,86,0.30); letter-spacing:0.01em;
  display:flex; align-items:center; justify-content:center; gap:8px;
  transition:transform 0.14s, box-shadow 0.18s, opacity 0.18s;
}
.ap__submit:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 8px 26px rgba(10,85,86,0.36); }
.ap__submit:disabled { opacity:.55; cursor:not-allowed; }
.ap__spinner {
  width:18px; height:18px; border-radius:50%;
  border:2.5px solid rgba(255,255,255,0.3); border-top-color:#fff;
  animation:ap__spin 0.75s linear infinite;
}

.ap__reset-btn {
  width:100%; margin-top:8px; padding:10px; border-radius:11px; cursor:pointer;
  font-size:13px; font-family:'Sora',sans-serif; font-weight:700;
  color:var(--ap-muted); background:transparent;
  border:1.5px solid var(--ap-border); transition:all 0.15s;
}
.ap__reset-btn:hover { border-color:rgba(239,68,68,0.3); color:var(--ap-danger); background:rgba(239,68,68,0.05); }
`;

/* ─── Component ─── */
function AddProduct() {
  const [newProduct, setNewProduct] = useState({ ...DEFAULT_PRODUCT });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const token = localStorage.getItem("auth_token");

  console.log("token", token);

  const updateProductField = (field, value) => {
    setNewProduct(prev => ({ ...prev, [field]: value }));
  };

  /* ── handleAddProduct (unchanged) ── */
  const handleAddProduct = async (e) => {
    e.preventDefault();

    const rate = parseFloat(newProduct.rate);
    const minOrderQty = parseFloat(newProduct.minOrderQty) || 1;
    const stockQty = parseFloat(newProduct.stockQty) || 0;
    const tax = parseFloat(newProduct.tax);
    const inventoryTracked = newProduct.inventoryTracked === "true";

    if (!newProduct.name.trim() || rate <= 0) {
      Swal.fire("Validation Error", "Product Name, HSN/SAC, and a positive Rate are required.", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("type", newProduct.type);
    formData.append("name", newProduct.name);
    formData.append("hsn", newProduct.hsn);
    formData.append("unit", newProduct.unit);
    formData.append("rate", rate);
    formData.append("tax", tax);
    formData.append("description", newProduct.description);
    formData.append("minOrderQty", minOrderQty);
    formData.append("stockQty", stockQty);
    formData.append("inventoryTracked", inventoryTracked);
    if (newProduct.imageFile) formData.append("image", newProduct.imageFile);

    console.log("--- Sending Product Data ---");
    for (let [key, value] of formData.entries()) console.log(`${key}:`, value);

    try {
      setIsSubmitting(true);
      const response = await axios.post(`${baseurl}addProduct`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        Swal.fire("Success!", `${newProduct.type} saved to master successfully.`, "success");
        if (newProduct.imagePreview) URL.revokeObjectURL(newProduct.imagePreview);
        setNewProduct({ ...DEFAULT_PRODUCT });
      } else {
        Swal.fire("Error", response.data.message || "Failed to save product", "error");
      }
    } catch (error) {
      console.error("ADD PRODUCT ERROR:", error);
      Swal.fire("Server Error", error.response?.data?.message || "Something went wrong while saving.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ── Dropzone (logic unchanged) ── */
  const DropzoneArea = () => {
    const onDrop = (acceptedFiles) => {
      if (acceptedFiles.length === 0) return;
      const file = acceptedFiles[0];
      if (newProduct.imagePreview) URL.revokeObjectURL(newProduct.imagePreview);
      const previewUrl = URL.createObjectURL(file);
      updateProductField("imageFile", file);
      updateProductField("imagePreview", previewUrl);
    };
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: { "image/*": [] }, multiple: false, maxSize: 4 * 1024 * 1024, onDrop,
    });
    return (
      <div {...getRootProps()} className={`ap__dropzone ${isDragActive ? "ap__dropzone--active" : ""}`}>
        <input {...getInputProps()} />
        <div className="ap__dropzone-icon"><FiUploadCloud /></div>
        {isDragActive ? (
          <div className="ap__dropzone-text"><strong>Drop the image here…</strong></div>
        ) : (
          <div className="ap__dropzone-text">
            <strong>Click to browse</strong> or drag & drop<br />
            <span style={{ fontSize:11 }}>PNG, JPG, WEBP · Max 4 MB</span>
          </div>
        )}
      </div>
    );
  };

  /* ── derived summary values ── */
  const rateVal = parseFloat(newProduct.rate) || 0;
  const taxVal  = parseFloat(newProduct.tax) || 0;
  const taxAmt  = (rateVal * taxVal) / 100;
  const totalRate = rateVal + taxAmt;

  /* ═══ RENDER ═══ */
  return (
    <>
      <style>{CSS}</style>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="ap__wrap">

          {/* ── Header ── */}
          <div className="ap__hd">
            <div className="ap__hd-icon"><FiPackage /></div>
            <div>
              <div className="ap__hd-title">Product &amp; Service Creation</div>
              <div className="ap__hd-sub">Define detailed specifications for your items and services</div>
            </div>
          </div>

          <form onSubmit={handleAddProduct}>
            <div className="ap__layout">

              {/* ── LEFT: Main form ── */}
              <div style={{ display:"flex", flexDirection:"column", gap:18 }}>

                {/* Item Type */}
                <div className="ap__card">
                  <div className="ap__card-hd">
                    <div className="ap__card-hd-icon"><FiBox size={13} /></div>
                    <div className="ap__card-hd-title">Item Type</div>
                  </div>
                  <div className="ap__card-body">
                    <div className="ap__type-row">
                      {["Product", "Service"].map(t => (
                        <button key={t} type="button"
                          className={`ap__type-btn ${newProduct.type === t ? "ap__type-btn--active" : ""}`}
                          onClick={() => updateProductField("type", t)}>
                          {t === "Product" ? <FiBox size={15} /> : <FiTool size={15} />}
                          {t === "Product" ? "Product (Goods)" : "Service (SAC)"}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Core Details */}
                <div className="ap__card">
                  <div className="ap__card-hd">
                    <div className="ap__card-hd-icon"><FiPackage size={13} /></div>
                    <div className="ap__card-hd-title">Core Details</div>
                  </div>
                  <div className="ap__card-body" style={{ display:"flex", flexDirection:"column", gap:14 }}>

                    <div className="ap__grid-2">
                      <div className="ap__field ap__full">
                        <label className="ap__label">{newProduct.type} Name <span className="ap__req">*</span></label>
                        <input type="text" className="ap__input"
                          value={newProduct.name}
                          onChange={(e) => updateProductField("name", e.target.value)}
                          placeholder={newProduct.type === "Product" ? "e.g., High-Grade Steel" : "e.g., Web Design Service"}
                          required />
                      </div>
                    </div>

                    <div className="ap__grid-3">
                      <div className="ap__field">
                        <label className="ap__label">{newProduct.type === "Product" ? "HSN Code" : "SAC Code"}</label>
                        <input type="text" className="ap__input"
                          value={newProduct.hsn}
                          onChange={(e) => updateProductField("hsn", e.target.value)}
                          placeholder={newProduct.type === "Product" ? "e.g., 7204" : "e.g., 9983"} />
                      </div>
                      <div className="ap__field">
                        <label className="ap__label">Unit of Measure</label>
                        <select className="ap__select" value={newProduct.unit}
                          onChange={(e) => updateProductField("unit", e.target.value)}>
                          {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </div>
                      <div className="ap__field">
                        <label className="ap__label">GST Tax %</label>
                        <select className="ap__select" value={newProduct.tax}
                          onChange={(e) => updateProductField("tax", e.target.value)} required>
                          {GST_SLABS.map(g => <option key={g} value={g}>{g}%</option>)}
                        </select>
                      </div>
                    </div>

                    <div className="ap__grid-2">
                      <div className="ap__field">
                        <label className="ap__label">Standard Rate <span className="ap__req">*</span></label>
                        <div className="ap__amount-wrap">
                          <span className="ap__amount-icon"><FaIndianRupeeSign size={12} /></span>
                          <input type="number" step="0.01" min="0" placeholder="0.00"
                            className="ap__input"
                            value={newProduct.rate}
                            onChange={(e) => updateProductField("rate", e.target.value)}
                            required />
                        </div>
                      </div>

                      {newProduct.type === "Product" && (
                        <div className="ap__field">
                          <label className="ap__label">Stock Qty (Available)</label>
                          <input type="number" min="0" placeholder="0"
                            className="ap__input"
                            value={newProduct.stockQty}
                            onChange={(e) => updateProductField("stockQty", e.target.value)} />
                        </div>
                      )}
                    </div>

                    {newProduct.type === "Product" && (
                      <div className="ap__grid-2">
                        <div className="ap__field">
                          <label className="ap__label">Min. Order Qty</label>
                          <input type="number" min="1" placeholder="1"
                            className="ap__input"
                            value={newProduct.minOrderQty}
                            onChange={(e) => updateProductField("minOrderQty", e.target.value)} />
                        </div>
                        <div className="ap__field" style={{ justifyContent:"flex-end" }}>
                          <label className="ap__label">Track Inventory</label>
                          <div className="ap__toggle-row">
                            <label className="ap__toggle-switch">
                              <input type="checkbox"
                                checked={newProduct.inventoryTracked === "true"}
                                onChange={(e) => updateProductField("inventoryTracked", e.target.checked ? "true" : "false")} />
                              <div className="ap__toggle-track" />
                              <div className="ap__toggle-thumb" />
                            </label>
                            <span className="ap__toggle-label">
                              {newProduct.inventoryTracked === "true" ? "Enabled" : "Disabled"}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                  </div>
                </div>

                {/* Description + Image */}
                <div className="ap__card">
                  <div className="ap__card-hd">
                    <div className="ap__card-hd-icon"><FiUploadCloud size={13} /></div>
                    <div className="ap__card-hd-title">Description &amp; Image</div>
                  </div>
                  <div className="ap__card-body" style={{ display:"grid", gridTemplateColumns:"1fr 220px", gap:18 }}>

                    <div className="ap__field">
                      <label className="ap__label">Description (for invoicing)</label>
                      <textarea className="ap__textarea" rows="4"
                        value={newProduct.description}
                        onChange={(e) => updateProductField("description", e.target.value)}
                        placeholder="Detailed notes on the product/service, often used for printing on invoices." />
                    </div>

                    <div className="ap__field">
                      <label className="ap__label">Product Image</label>
                      {newProduct.imagePreview ? (
                        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:10 }}>
                          <div className="ap__img-preview">
                            <img src={newProduct.imagePreview} alt="Preview" />
                            <button type="button" className="ap__img-remove"
                              onClick={() => {
                                URL.revokeObjectURL(newProduct.imagePreview);
                                updateProductField("imagePreview", "");
                                updateProductField("imageFile", null);
                              }}>
                              <FiX size={11} />
                            </button>
                          </div>
                          <span style={{ fontSize:11, color:"var(--ap-muted)" }}>{newProduct.imageFile?.name}</span>
                        </div>
                      ) : (
                        <DropzoneArea />
                      )}
                    </div>

                  </div>
                </div>

              </div>

              {/* ── RIGHT: Summary + Submit ── */}
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

                {/* Summary card */}
                <div className="ap__card">
                  <div className="ap__card-hd">
                    <div className="ap__card-hd-icon" style={{ background:"linear-gradient(135deg,#b45309,var(--ap-gold))" }}>
                      <FaIndianRupeeSign size={13} />
                    </div>
                    <div className="ap__card-hd-title">Item Summary</div>
                  </div>
                  <div className="ap__card-body">
                    <div className="ap__summary-row">
                      <span className="ap__summary-lbl">Type</span>
                      <span className={`ap__type-chip ${newProduct.type === "Product" ? "ap__type-chip--product" : "ap__type-chip--service"}`}>
                        {newProduct.type}
                      </span>
                    </div>
                    <div className="ap__summary-row">
                      <span className="ap__summary-lbl">Name</span>
                      <span className="ap__summary-val" style={{ maxWidth:150, textAlign:"right", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {newProduct.name || <span style={{ color:"#b0cece" }}>—</span>}
                      </span>
                    </div>
                    <div className="ap__summary-row">
                      <span className="ap__summary-lbl">Base Rate</span>
                      <span className="ap__summary-val">
                        {rateVal > 0 ? `₹${rateVal.toLocaleString("en-IN", { minimumFractionDigits:2 })}` : <span style={{ color:"#b0cece" }}>—</span>}
                      </span>
                    </div>
                    <div className="ap__summary-row">
                      <span className="ap__summary-lbl">GST ({newProduct.tax}%)</span>
                      <span className="ap__summary-val">
                        {rateVal > 0 ? `₹${taxAmt.toLocaleString("en-IN", { minimumFractionDigits:2 })}` : <span style={{ color:"#b0cece" }}>—</span>}
                      </span>
                    </div>
                    <div className="ap__summary-row" style={{ paddingTop:12 }}>
                      <span className="ap__summary-lbl" style={{ fontWeight:700, color:"var(--ap-text)" }}>Total Rate</span>
                      <span className="ap__summary-val ap__summary-val--teal" style={{ fontSize:16 }}>
                        {rateVal > 0 ? `₹${totalRate.toLocaleString("en-IN", { minimumFractionDigits:2 })}` : <span style={{ color:"#b0cece", fontSize:13 }}>—</span>}
                      </span>
                    </div>
                    {newProduct.type === "Product" && (
                      <div className="ap__summary-row">
                        <span className="ap__summary-lbl">Stock Qty</span>
                        <span className="ap__summary-val">{newProduct.stockQty || "0"}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit */}
                <div className="ap__card">
                  <div className="ap__card-body">
                    <button type="submit" className="ap__submit" disabled={isSubmitting}>
                      {isSubmitting ? <div className="ap__spinner" /> : <FiPlus size={16} />}
                      {isSubmitting ? "Saving…" : `Save ${newProduct.type} to Master`}
                    </button>
                    <button type="button" className="ap__reset-btn"
                      onClick={() => { if (newProduct.imagePreview) URL.revokeObjectURL(newProduct.imagePreview); setNewProduct({ ...DEFAULT_PRODUCT }); }}>
                      Reset Form
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </form>

        </div>
      </main>
    </>
  );
}

export default AddProduct;
